import './private-route.css';

import React, { LazyExoticComponent, useEffect, useState } from 'react';

import AuthService from '../services/AuthService';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import OverlaySpinner from '../shared/OverlaySpinner';
import { Route } from 'react-router-dom';
import TopMenu from '../shared/TopMenu';
import Typography from '@mui/material/Typography';
import { User } from 'oidc-client';

/** Components composition passed to route. */
type ComponentFactory =
  | (() => JSX.Element)
  | LazyExoticComponent<() => JSX.Element>;

/**
 * Renders the specified component on a route, ensuring the user has
 * authenticated using `AuthService`. Components within `PrivateRoute` can
 * assume that the `AuthService` singleton has been loaded successfully. If the
 * user is not logged in - i.e., the backend refresh token is invalid or
 * missing - then the user will be directed to the login page.
 */
const PrivateRoute = ({
  component,
  header,
  ...rest
}: {
  /** Component to render after validating the user's credentials. */
  component: ComponentFactory;
  /** Optional value that can be set to `false` to render the page without header. */
  header?: boolean;
  /** Optional value indicating whether the route match must be exact. */
  exact?: boolean;
  /** Routing path for which to display the component. */
  path: string;
}) => {
  /** Track any login error. */
  const [loginError, setLoginError] = useState<string | undefined>();
  /** Authenticated user, here as state to trigger redraw on login. */
  const [user, setUser] = useState<User | null>(null);
  const [mfaVerified, setMfaVerified] = useState<boolean | null>(null);

  /** Authenticate if necessary. */
  useEffect(() => {
    if (window.location.pathname.startsWith('/mfa')) {
      return;
    }
    new Promise<void>((resolve, reject) => {
      // If a code is in the search params, need to exchange it first.
      if (!!new URLSearchParams(document.location.search).get('code')) {
        AuthService.login().then(resolve).catch(reject);
      } else {
        resolve();
      }
    })
      .then(() => AuthService.getUser())
      .then(async (authUser) => {
        if (!authUser) {
          await AuthService.login();
          return;
        }

        setUser(authUser);

        // Check MFA status — server redirects to /mfa/select-user if not verified
        const mfaRes = await fetch('/mfa/status');
        const text = await mfaRes.text();
        try {
          const data = JSON.parse(text) as { verified: boolean };
          if (data.verified) {
            setMfaVerified(true);
          } else {
            window.location.href = '/mfa/select-user';
          }
        } catch (e) {
          console.error('Failed to parse MFA status JSON:', e);
          await AuthService.login();
        }
      })
      .catch((reason: any) => setLoginError(`${reason}`));
  }, []);

  const renderFn = (Component: ComponentFactory) => (props: any) => (
    <>
      {loginError ? (
        <div className="private-route-error">
          <Typography variant="body1">Unable to log in.</Typography>
          <Typography variant="body1">
            Please try reloading the page.
          </Typography>
          <Typography variant="caption">Reason: {loginError}</Typography>
        </div>
      ) : !user || mfaVerified === null ? (
        <OverlaySpinner size={24} />
      ) : (
        <div className="private-route-root">
          {header !== false && (
            <header>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid>
                  <TopMenu />
                </Grid>
                {AuthService.user && (
                  <Grid>
                    {AuthService.user.profile.given_name}{' '}
                    {AuthService.user.profile.family_name}
                  </Grid>
                )}
                <Grid>
                  <Button color="primary" onClick={() => AuthService.logout()}>
                    Logout
                  </Button>
                </Grid>
              </Grid>
            </header>
          )}
          <div className="private-route-content">
            <Component {...props} />
          </div>
        </div>
      )}
    </>
  );

  return <Route {...rest} render={renderFn(component)} />;
};

export default PrivateRoute;
